/* ============================================================
   index.css
   역할: 전체 앱의 전역 스타일 정의 (Premium Copilot Overhaul)
   TailwindCSS 기본 디렉티브 + 아발론(Glassmorphism) 테마
   ============================================================ */

@import url('https://fonts.googleapis.com/css2?family=Pretendard:wght@300;400;500;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');

/* Tailwind 3 기본 디렉티브 */
@tailwind base;
@tailwind components;
@tailwind utilities;

/* ── 프리미엄 전역 변수 & 기본 설정 ──────────────────────────────────── */
@layer base {
  :root {
    --glass-bg: rgba(15, 23, 42, 0.4);
    --glass-border: rgba(255, 255, 255, 0.08);
    --glass-highlight: rgba(255, 255, 255, 0.03);
    
    /* 신비롭고 깊은 그라데이션 */
    --gradient-glow: linear-gradient(145deg, rgba(88, 28, 135, 0.5), rgba(55, 48, 163, 0.5), transparent);
  }

  * {
    box-sizing: border-box;
  }

  html {
    font-family: 'Pretendard', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    scroll-behavior: smooth;
  }

  body {
    /* 기존 단색 배경을 고급스러운 Radial Gradient로 변경 */
    background: radial-gradient(circle at 15% 50%, rgba(30, 27, 75, 1) 0%, rgba(15, 23, 42, 1) 40%, rgba(2, 6, 23, 1) 100%);
    background-attachment: fixed;
    color: #f1f5f9;
    margin: 0;
    padding: 0;
    min-width: 1024px;
  }

  /* 텍스트 선택 색상 */
  ::selection {
    @apply bg-indigo-500/40 text-indigo-100;
  }

  /* 입력창 하이라이트 제거 */
  input:focus, textarea:focus {
    outline: none;
  }
}

/* ── 커스텀 스크롤바 ────────────────────────────── */
@layer utilities {
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    @apply bg-slate-700/50 rounded-full;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    @apply bg-indigo-500/50;
  }

  /* 스크롤바 완전히 숨기기 (스크롤은 여전히 가능) */
  .scrollbar-hide {
    -ms-overflow-style: none;  /* IE, Edge */
    scrollbar-width: none;     /* Firefox */
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;             /* Chrome, Safari, Opera */
  }
}

/* ── 커스텀 글래스 & 패널 클래스 ─────────────────── */
@layer components {
  .glass-panel {
    @apply bg-[rgba(15,23,42,0.4)] backdrop-blur-xl border border-[rgba(255,255,255,0.06)] shadow-2xl;
  }
  
  .glass-card {
    @apply bg-[rgba(30,41,59,0.3)] backdrop-blur-md border border-[rgba(255,255,255,0.05)] shadow-lg hover:border-[rgba(255,255,255,0.1)] hover:bg-[rgba(30,41,59,0.4)] transition-all duration-300;
  }
  
  .glass-input {
    @apply bg-[rgba(15,23,42,0.6)] backdrop-blur-sm border border-[rgba(255,255,255,0.1)] focus:border-indigo-500/60 focus:bg-[rgba(15,23,42,0.8)] shadow-inner transition-all duration-200;
  }
  
  .gradient-text {
    @apply bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 via-purple-300 to-indigo-300 animate-gradient-x;
  }
}

/* ── 입력창 폼 초기화 ─────────────────────────────── */
textarea::-webkit-resizer {
  display: none;
}

input:-webkit-autofill,
textarea:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px #0f172a inset;
  -webkit-text-fill-color: #f1f5f9;
  transition: background-color 5000s ease-in-out 0s;
}

/* ── 세련된 애니메이션 유틸리티 ─────────────────────── */
@layer utilities {
  .animate-fade-in {
    animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  
  .animate-fade-in-up {
    animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .animate-pulse-slow {
    animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  .animate-gradient-x {
    background-size: 200% 200%;
    animation: gradientX 5s ease infinite;
  }
  
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes gradientX {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
  }
}
